'use client';

/**
 * Chat input form component (active conversation)
 *
 * A thin adapter over the shared `MeridianComposer` — the SAME composer the
 * start screen renders — bound in controlled mode because the active chat owns
 * the draft (suggested messages and retry write into it). All affordances
 * (files, screenshots, skills, connectors, inline model switch) come from the
 * shared component, so the two surfaces cannot drift apart.
 */

import React, { memo, useCallback } from 'react';
import { FilePart } from '../../lib/db/types.js';
import type { AttachedSkill } from '../skills/types.js';
import { MeridianComposer } from './composer/MeridianComposer.js';

interface ChatInputFormProps {
  input: string;
  onInputChange: (value: string) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>, files?: FilePart[]) => Promise<void>;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  disabled: boolean;
  /** True while a request is in flight OR streaming — swaps Send for Stop. */
  isBusy: boolean;
  /** Abort the in-flight generation. */
  onStop: () => void;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  chatId: string;
  onFilesSelected?: (files: FilePart[]) => void;
  /** Hub Meridian API base (skills always resolve via /api/meridian). */
  apiBaseUrl?: string;
  attachedSkill?: AttachedSkill | null;
  onAttachSkill?: (skill: AttachedSkill) => void;
  onClearAttachedSkill?: () => void;
  /** MCP connections page — surfaced in the "+" menu when provided. */
  mcpConnectionsHref?: string;
}

function ChatInputFormComponent({
  input,
  onInputChange,
  onSubmit,
  onKeyDown,
  disabled,
  isBusy,
  onStop,
  inputRef,
  chatId,
  onFilesSelected,
  attachedSkill = null,
  onClearAttachedSkill,
  mcpConnectionsHref,
  apiBaseUrl,
}: ChatInputFormProps) {
  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>, files: FilePart[]) => {
      await onSubmit(e, files);
    },
    [onSubmit]
  );

  return (
    <MeridianComposer
      chatId={chatId}
      apiBaseUrl={apiBaseUrl}
      inputRef={inputRef}
      value={input}
      onValueChange={onInputChange}
      onSubmit={handleSubmit}
      onKeyDown={onKeyDown}
      disabled={disabled}
      isBusy={isBusy}
      onStop={onStop}
      attachedSkill={attachedSkill}
      onClearAttachedSkill={onClearAttachedSkill}
      onFilesSelected={onFilesSelected}
      mcpConnectionsHref={mcpConnectionsHref}
    />
  );
}

export const ChatInputForm = memo(ChatInputFormComponent);
